/**
 * Date utilities
 * Pure functions for working with dates
 */
export interface ParsedDateParts {
    weekday: string; // monday, tuesday
    day: number;
    month: string; // january, february
    year: number;
}
export class DateHelper {
    /**
     * Generate array of months (01-12)
     * @returns Array of objects with value and label for each month
     * @example
     * DateHelper.getMonths()
     * // Returns: [{ value: "01", label: "01" }, ..., { value: "12", label: "12" }]
     */
    static getMonths(): Array<{value: string; label: string}> {
        return Array.from({length: 12}, (_, i) => {
            const month = (i + 1).toString().padStart(2, "0");
            return {value: month, label: month};
        });
    }

    /**
     * Generate array of years starting from current year
     * @param count Number of years to generate (default: 12)
     * @param startYear Optional start year (default: current year)
     * @returns Array of objects with value and label for each year
     * @example
     * DateHelper.getYears(10)
     * // Returns: [{ value: "2024", label: "2024" }, ..., { value: "2033", label: "2033" }]
     */
    static getYears(count: number = 12, startYear?: number): Array<{value: string; label: string}> {
        const start = startYear ?? new Date().getFullYear();
        return Array.from({length: count}, (_, i) => {
            const year = (start + i).toString();
            return {value: year, label: year};
        });
    }

    /**
     * Get current year
     * @returns Current year as number
     */
    static getCurrentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Get current month (1-12)
     * @returns Current month as number
     */
    static getCurrentMonth(): number {
        return new Date().getMonth() + 1;
    }

    /**
     * Get current month as padded string (01-12)
     * @returns Current month as string
     */
    static getCurrentMonthString(): string {
        return this.getCurrentMonth().toString().padStart(2, "0");
    }

    /**
     * Format date to YYYY-MM-DD
     * @param date Date to format
     * @returns Formatted date string
     */
    static formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    /**
     * Format date to YYYY-MM-DD, handling Date, string, null, or undefined
     * If already a string in correct format, returns as-is
     * @param date Date, string, null, or undefined
     * @returns Formatted date string or undefined if input is null/undefined
     * @example
     * DateHelper.toDateString(new Date("2024-12-15")) // => "2024-12-15"
     * DateHelper.toDateString("2024-12-15") // => "2024-12-15"
     * DateHelper.toDateString(null) // => undefined
     */
    static toDateString(date: Date | string | null | undefined): string | undefined {
        if (!date) return undefined;
        if (date instanceof Date) return this.formatDate(date);
        return date;
    }

    /**
     * Convert Date or string to MM-DD-YYYY format (for URL compatibility with datepicker)
     * @param date - Date object or string
     * @returns Formatted date string in MM-DD-YYYY format
     * @example
     * DateHelper.formatDateForUrl(new Date("2024-12-15"))
     * // => "12-15-2024"
     * DateHelper.formatDateForUrl("2024-12-15")
     * // => "12-15-2024"
     */
    static formatDateForUrl(date: Date | string): string {
        if (date instanceof Date) {
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
        }
        // If string is in YYYY-MM-DD format, convert to MM-DD-YYYY
        if (date?.includes("-") && date.split("-")[0].length === 4) {
            const [year, month, day] = date.split("-");
            return `${month}-${day}-${year}`;
        }
        return date;
    }

    /**
     * Check if a date is in the past
     * @param date Date to check
     * @returns True if date is in the past
     */
    static isPast(date: Date): boolean {
        return date < new Date();
    }

    /**
     * Check if a date is in the future
     * @param date Date to check
     * @returns True if date is in the future
     */
    static isFuture(date: Date): boolean {
        return date > new Date();
    }

    /**
     * Get number of days between two dates
     * @param date1 First date
     * @param date2 Second date
     * @returns Number of days between dates
     */
    static daysBetween(date1: Date, date2: Date): number {
        const diffTime = Math.abs(date2.getTime() - date1.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    /**
     * Calculate months since a given date
     * @param startDate - Start date (Date object or ISO string)
     * @returns Number of full months since the start date
     * @example
     * DateHelper.monthsSince("2025-06-15") // => 6 (if current date is 2025-12-30)
     * DateHelper.monthsSince("2025-12-15") // => 0
     */
    static monthsSince(startDate: Date | string): number {
        const start = typeof startDate === "string" ? new Date(startDate) : startDate;
        const now = new Date();

        const years = now.getFullYear() - start.getFullYear();
        const months = now.getMonth() - start.getMonth();

        return years * 12 + months;
    }

    /**
     * Calculate full (day-aware) months from now until a future date
     * @param targetDate - Future date (Date object or ISO string)
     * @returns Number of full months until the target date (never negative)
     * @example
     * DateHelper.monthsUntil("2027-02-07") // => 6 (if current date is 2026-07-29)
     */
    static monthsUntil(targetDate: Date | string): number {
        const target = typeof targetDate === "string" ? new Date(targetDate) : targetDate;
        const now = new Date();

        let months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
        if (target.getDate() < now.getDate()) {
            months -= 1;
        }

        return Math.max(months, 0);
    }

    /**
     * Format date range as "Mon DD - Mon DD"
     * @param startDate Start date
     * @param endDate End date
     * @returns Formatted date range string or empty string if dates are null
     * @example
     * DateHelper.formatDateRange(new Date("2024-12-15"), new Date("2024-12-20"))
     * // Returns: "Dec 15 - Dec 20"
     */
    static formatDateRange(startDate: Date | null, endDate: Date | null): string {
        if (!startDate || !endDate) return "";

        const formatDate = (date: Date): string => {
            return date.toLocaleDateString("en-US", {month: "short", day: "numeric"});
        };

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    /**
     * Format date range in compact format
     * - Same month: "Jul 10-14"
     * - Different months: "Jul 10 - Aug 14"
     * - Single date (no end): "Jul 10"
     * @param startDate Start date
     * @param endDate End date (optional)
     * @returns Formatted date range string or undefined if no start date
     * @example
     * DateHelper.formatDateRangeCompact(new Date("2024-07-10"), new Date("2024-07-14"))
     * // Returns: "Jul 10-14"
     * DateHelper.formatDateRangeCompact(new Date("2024-07-10"), new Date("2024-08-14"))
     * // Returns: "Jul 10 - Aug 14"
     */
    static formatDateRangeCompact(startDate: Date | null, endDate?: Date | null): string | undefined {
        if (!startDate) return undefined;

        const formatMonth = (date: Date): string => {
            return date.toLocaleDateString("en-US", {month: "short"});
        };

        const formatDay = (date: Date): string => {
            return date.getDate().toString();
        };

        if (endDate) {
            const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();

            if (sameMonth) {
                // Same month: "Jul 10-14"
                return `${formatMonth(startDate)} ${formatDay(startDate)}-${formatDay(endDate)}`;
            }
            // Different months: "Jul 10 - Aug 14"
            return `${formatMonth(startDate)} ${formatDay(startDate)} - ${formatMonth(endDate)} ${formatDay(endDate)}`;
        }

        // Single date: "Jul 10"
        return `${formatMonth(startDate)} ${formatDay(startDate)}`;
    }

    /**
     * Format date range with the year shown once, on the end date: "Oct 6 - Oct 9, 2026"
     * @param startDate Start date
     * @param endDate End date
     * @returns Formatted date range string, or empty string if either date is null
     * @example
     * DateHelper.formatDateRangeWithYear(new Date(2026, 9, 6), new Date(2026, 9, 9))
     * // Returns: "Oct 6 - Oct 9, 2026"
     */
    static formatDateRangeWithYear(startDate: Date | null, endDate: Date | null): string {
        if (!startDate || !endDate) return "";

        const formatShort = (date: Date): string => date.toLocaleDateString("en-US", {month: "short", day: "numeric"});
        const formatWithYear = (date: Date): string => date.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});

        return `${formatShort(startDate)} - ${formatWithYear(endDate)}`;
    }

    /**
     * Format date with weekday and year as "Wed, Dec 18, 2024"
     * @param date Date object or ISO date string
     * @returns Formatted date string
     * @example
     * DateHelper.formatWithWeekday("2024-12-18")
     * // Returns: "Wed, Dec 18, 2024"
     */
    static formatWithWeekday(date: Date | string): string {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    }

    /**
     * Calculate number of nights between check-in and check-out dates
     * @param checkIn Check-in date (Date or ISO string)
     * @param checkOut Check-out date (Date or ISO string)
     * @returns Number of nights
     */
    static calculateNights(checkIn: Date | string, checkOut: Date | string): number {
        const start = typeof checkIn === "string" ? new Date(checkIn) : checkIn;
        const end = typeof checkOut === "string" ? new Date(checkOut) : checkOut;
        const diffTime = end.getTime() - start.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    /**
     * Format time from 24-hour format to 12-hour format with AM/PM
     * @param time24 Time string in 24-hour format (HH:MM:SS or HH:MM)
     * @returns Formatted time string in 12-hour format (e.g., "4:00 PM")
     * @example
     * DateHelper.formatTime24to12("16:00:00") // => "4:00 PM"
     * DateHelper.formatTime24to12("10:00:00") // => "10:00 AM"
     * DateHelper.formatTime24to12("00:30") // => "12:30 AM"
     */
    static formatTime24to12(time24: string | null | undefined): string {
        // Default to 12:00 PM if time is not provided
        if (!time24) return "12:00 PM";

        const parts = time24.split(":");
        if (parts.length < 2) return "12:00 PM";

        const [hours, minutes] = parts;
        const hour = parseInt(hours, 10);

        // Validate hour (0-23) and minutes (0-59)
        if (isNaN(hour) || hour < 0 || hour > 23) return "12:00 PM";
        if (!minutes || isNaN(parseInt(minutes, 10))) return "12:00 PM";

        const isPM = hour >= 12;
        const hour12 = hour % 12 || 12;
        const period = isPM ? "PM" : "AM";
        return `${hour12}:${minutes} ${period}`;
    }

    /**
     * Format date with full weekday name
     * @param date Date object or ISO date string
     * @returns Full weekday name (e.g., "Wednesday")
     * @example
     * DateHelper.formatWeekday("2024-12-18") // => "Wednesday"
     */
    static formatWeekday(date: Date | string): string {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleDateString("en-US", {weekday: "long"});
    }

    /**
     * Format date with month, day, and year
     * @param date Date object or ISO date string
     * @returns Formatted date (e.g., "Dec 18, 2024")
     * @example
     * DateHelper.formatDateLong("2024-12-18") // => "Dec 18, 2024"
     */
    static formatDateLong(date: Date | string): string {
        const d = typeof date === "string" ? new Date(date) : date;
        return d.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
    }

    /**
     * Parse date from various formats to YYYY-MM-DD (ISO format)
     * Handles: YYYY-MM-DD, MM-DD-YYYY, MM/DD/YYYY
     * @param dateStr - Date string in various formats
     * @returns Date string in YYYY-MM-DD format or null if invalid
     * @example
     * DateHelper.parseDateToISO("12-15-2024") // => "2024-12-15"
     * DateHelper.parseDateToISO("12/15/2024") // => "2024-12-15"
     * DateHelper.parseDateToISO("2024-12-15") // => "2024-12-15"
     */
    static parseDateToISO(dateStr: string | null | undefined): string | null {
        if (!dateStr) return null;

        // Already in YYYY-MM-DD format
        if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
            return dateStr;
        }

        // MM-DD-YYYY format → YYYY-MM-DD
        if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
            const [month, day, year] = dateStr.split("-");
            return `${year}-${month}-${day}`;
        }

        // MM/DD/YYYY format → YYYY-MM-DD
        if (dateStr.includes("/")) {
            const [month, day, year] = dateStr.split("/");
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }

        return dateStr;
    }

    /**
     * Parse date string to Date in LOCAL timezone (not UTC)
     * IMPORTANT: new Date("2026-01-21") parses as UTC midnight, causing day shift in negative timezones (US)
     * This method correctly parses the date in local timezone.
     * Supports YYYY-MM-DD, MM-DD-YYYY, and ISO formats.
     * @param dateStr - Date string in YYYY-MM-DD, MM-DD-YYYY, or ISO format
     * @returns Date object in local timezone, or null if invalid
     * @example
     * DateHelper.parseLocalDate("2026-01-21") // => Jan 21, 2026 (YYYY-MM-DD)
     * DateHelper.parseLocalDate("01-21-2026") // => Jan 21, 2026 (MM-DD-YYYY)
     * DateHelper.parseLocalDate("invalid") // => null
     */
    static parseLocalDate(dateStr: unknown): Date | null {
        if (!dateStr || typeof dateStr !== "string") {
            return null;
        }

        try {
            // Handle ISO format (2026-01-21T00:00:00.000Z) - extract date part only
            let datePart = dateStr;
            if (dateStr.includes("T")) {
                datePart = dateStr.split("T")[0];
            }

            const parts = datePart.split("-").map(Number);
            if (parts.length !== 3 || parts.some(isNaN)) {
                return null;
            }

            let year: number, month: number, day: number;

            // If first part is 4 digits, it's YYYY-MM-DD format
            if (datePart.split("-")[0].length === 4) {
                [year, month, day] = parts;
            } else {
                // Otherwise it's MM-DD-YYYY format
                [month, day, year] = parts;
            }

            const date = new Date(year, month - 1, day);

            // Validate the date is real (e.g., not Feb 30)
            if (isNaN(date.getTime())) {
                return null;
            }

            return date;
        } catch {
            return null;
        }
    }
    static parseDateParts(date: Date | string): ParsedDateParts {
        const d = typeof date === "string" ? new Date(date) : date;

        return {
            weekday: d.toLocaleDateString("en-US", {weekday: "long"}).toLowerCase(),
            day: d.getDate(),
            month: d.toLocaleDateString("en-US", {month: "long"}).toLowerCase(),
            year: d.getFullYear(),
        };
    }
}
